/**
 * Run-input fingerprint for tile cache keys (S10). Hashes the RAW inputs a
 * tile run depends on — the tile's own data inputs (connection, database,
 * SQL, result-set pick: exactly what the S6 edit dialog treats as a query
 * change) plus the used placeholder names, their current raw values, and
 * the relevant definition shapes — NOT the resolved values: fixed time-range
 * windows resolve a fresh `now` per run, which must never churn the cache
 * key. `useTileResult` and `runTileManually` share this so both address the
 * same cache entry, and a definition change from ANY writer (edit dialog,
 * MCP `dashboard_update_tile`, import) re-keys the tile by construction —
 * keyed on parameters alone, a manual-refresh tile edited over MCP kept
 * serving the previous SQL's cached rows.
 */
import type { BoundParams, DashboardParameter, DashboardTile, NoteTile } from '../types/dashboardTypes';
import { DASHBOARD_FILTERS_NAME } from './parameterBinding';
import { placeholderNames } from './sqlPlaceholders';
import { stableValueKey } from './dashboardTileCache';

type TileRunInputs = Pick<
  Exclude<DashboardTile, NoteTile>,
  'connectionId' | 'database' | 'source' | 'resultSetIndex'
>;

export function tileRunInputsHash(args: {
  tile: TileRunInputs;
  engine: string;
  parameters: DashboardParameter[];
  values: BoundParams;
}): string {
  const { tile } = args;
  const source = {
    connectionId: tile.connectionId,
    database: tile.database ?? null,
    sql: tile.source.sql,
    resultSetIndex: tile.resultSetIndex ?? 0,
  };
  const used = placeholderNames(tile.source.sql, args.engine)
    .filter((n) => n !== DASHBOARD_FILTERS_NAME)
    .sort();
  const params: Record<string, unknown> = {};
  for (const name of used) {
    const key = name === 'range_start' || name === 'range_end' ? 'range' : name;
    const def = args.parameters.find((p) => (p.kind === 'time-range' ? key === 'range' : p.id === key));
    params[name] = {
      value: args.values[key] ?? null,
      def: def ?? null, // defaults/valueType/multi shape changes re-key too
    };
  }
  return stableValueKey({ source, params });
}
